import {Issue, IssueLine, TimestampKeyTuple} from './types';
import {getXValueForTimestamp} from './utils';

export const initialState: IssuesByTimestampState = {
  issues: [],
  issuesByCacheKey: {},
  issuesByRunId: {},
  timestamps: {},
};

type Actions =
  | {
      type: 'fetchTimestamps-start';
      payload: {xAxis: string; cacheKey: string};
    }
  | {
      type: 'fetchTimestamps-error';
      payload: {xAxis: string; cacheKey: string; error: Error};
    }
  | {
      type: 'fetchTimestamps-success';
      payload: {
        xAxis: string;
        cacheKey: string;
        results: TimestampKeyTuple[];
      };
    }
  | {
      type: 'addIssues';
      payload: {
        since: number;
        issues: Issue[];
      };
    }
  | {
      type: 'clearIssues';
      payload: {
        targets: {
          runId: string;
          since: number;
        }[];
      };
    };

export function issuesByTimestampReducer(
  state: IssuesByTimestampState,
  action: Actions
) {
  switch (action.type) {
    case 'addIssues': {
      const savedIssues = (state.issues ?? [])
        .filter(issue => issue.timestamp < action.payload.since)
        .concat(action.payload.issues);

      // Create a new object to hold filtered issues
      const savedIssuesByRunId: IssuesByTimestampState['issuesByRunId'] = {};

      // Copy and filter existing issues
      Object.keys(state.issuesByRunId).forEach(runId => {
        const filteredIssues = state.issuesByRunId[runId].filter(
          issue => issue.timestamp < action.payload.since
        );
        if (filteredIssues.length > 0) {
          savedIssuesByRunId[runId] = filteredIssues;
        }
      });

      // Sort the new issues (create a copy to avoid mutating the input)
      const sortedIssues = [...action.payload.issues].sort(
        (a, b) => a.timestamp - b.timestamp
      );

      // Group new issues by runId first
      const newIssuesByRunId: Record<string, Issue[]> = {};
      sortedIssues.forEach(issue => {
        if (!newIssuesByRunId[issue.runId]) {
          newIssuesByRunId[issue.runId] = [];
        }
        newIssuesByRunId[issue.runId].push(issue);
      });

      // Merge with existing issues
      Object.keys(newIssuesByRunId).forEach(runId => {
        savedIssuesByRunId[runId] = [
          ...(savedIssuesByRunId[runId] || []),
          ...newIssuesByRunId[runId],
        ];
      });
      return {
        ...state,
        issues: savedIssues,
        issuesByRunId: savedIssuesByRunId,
      };
    }
    case 'clearIssues': {
      return {
        ...state,
        issues: [],
        issuesByRunId: {},
        issuesByCacheKey: {},
      };
    }
    case 'fetchTimestamps-start': {
      const thisRecord = state.timestamps[action.payload.cacheKey];
      return {
        ...state,
        timestamps: {
          ...state.timestamps,
          [action.payload.cacheKey]: {
            ...thisRecord,
            error: null,
            loading: true,
            keyByTimestamp: thisRecord?.keyByTimestamp ?? [],
          },
        },
      };
    }
    case 'fetchTimestamps-error': {
      return {
        ...state,
        timestamps: {
          ...state.timestamps,
          [action.payload.cacheKey]: {
            error: action.payload.error,
            loading: false,
            keyByTimestamp: [],
          },
        },
      };
    }
    case 'fetchTimestamps-success': {
      const convertedIssuesList = convertIssuesToTimestampKeyTuples(
        state.issues,
        action.payload.results
      );
      return {
        ...state,
        issuesByCacheKey: {
          ...state.issuesByCacheKey,
          [action.payload.cacheKey]: convertedIssuesList,
        },
        timestamps: {
          ...state.timestamps,
          [action.payload.cacheKey]: {
            error: null,
            loading: false,
            keyByTimestamp: action.payload.results,
          },
        },
      };
    }

    default: {
      return state;
    }
  }
}

export function convertIssuesToTimestampKeyTuples(
  issues: Issue[],
  keyByTimestamp: TimestampKeyTuple[]
) {
  const convertedIssuesList: IssueLine[] = [];
  let searchStartIndex = 0; // Track index instead of slicing arrays

  issues.forEach(issue => {
    // Use optimized search that starts from our last position
    const result = getXValueForTimestamp(
      keyByTimestamp,
      issue.timestamp,
      searchStartIndex
    );
    if (result === undefined) {
      // this represents an issue outside the range of this zoom
      return;
    }
    searchStartIndex = result.bottomIndex; // Update search start position
    convertedIssuesList.push({
      left: 0,
      id: issue.id,
      timestamp: issue.timestamp,
      xValue: result.value,
    });
  });
  return convertedIssuesList;
}

type CacheKey = string;

type TimestampDetail = {
  error: Error | null;
  loading: boolean;
  keyByTimestamp: TimestampKeyTuple[];
};

type IssuesByTimestampState = {
  issues: Issue[];
  issuesByCacheKey: Record<CacheKey, IssueLine[]>;
  issuesByRunId: Record<string, Issue[]>;
  timestamps: Record<CacheKey, TimestampDetail>;
};
